import { useState, useRef, useEffect } from "react";

type SortOption = "featured" | "best-seller" | "price-asc" | "price-desc" | "newest" | "rating" | "sale" | "limited-stock";
type ViewMode = "grid" | "list";

interface Props {
  activeSort: SortOption;
  onSortChange: (s: SortOption) => void;
  resultCount: number;
  viewMode: ViewMode;
  onViewModeChange: (v: ViewMode) => void;
}

const sortOptions: { id: SortOption; label: string; icon: string; color: string; badge?: string }[] = [
  { id: "featured", label: "Featured", icon: "ri-star-line", color: "text-gray-400" },
  { id: "best-seller", label: "Best Sellers First", icon: "ri-trophy-line", color: "text-amber-500", badge: "Best Seller" },
  { id: "newest", label: "Newest Arrivals", icon: "ri-sparkling-line", color: "text-emerald-500", badge: "New Arrival" },
  { id: "price-asc", label: "Price: Low to High", icon: "ri-arrow-up-line", color: "text-gray-400" },
  { id: "price-desc", label: "Price: High to Low", icon: "ri-arrow-down-line", color: "text-gray-400" },
  { id: "rating", label: "Top Rated", icon: "ri-star-fill", color: "text-amber-400" },
  { id: "sale", label: "On Sale", icon: "ri-price-tag-3-line", color: "text-red-500", badge: "Sale" },
  { id: "limited-stock", label: "Limited Stock First", icon: "ri-alarm-warning-line", color: "text-orange-500", badge: "Limited" },
];

export default function ShopFilters({ activeSort, onSortChange, resultCount, viewMode, onViewModeChange }: Props) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeOption = sortOptions.find((s) => s.id === activeSort) ?? sortOptions[0];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <span className="font-medium text-gray-700">{resultCount}</span>
        <span>product{resultCount !== 1 ? "s" : ""}</span>
      </div>

      <div className="flex items-center gap-3">
        {/* Custom Sort Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setOpen(!open)}
            className="flex items-center gap-2 text-sm border border-gray-200 rounded-md px-3 py-1.5 bg-white text-gray-700 cursor-pointer hover:border-amber-400 transition-colors whitespace-nowrap"
          >
            <span className="hidden sm:inline text-xs text-gray-400 font-medium">Sort:</span>
            <div className={`w-4 h-4 flex items-center justify-center ${activeOption.color}`}>
              <i className={activeOption.icon}></i>
            </div>
            <span className="font-medium">{activeOption.label}</span>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400 ml-0.5">
              <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
            </div>
          </button>

          {open && (
            <div className="absolute right-0 top-full mt-1.5 w-56 bg-white border border-gray-200 rounded-lg overflow-hidden z-50">
              <div className="px-3 py-2 border-b border-gray-100">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Sort by</p>
              </div>
              {sortOptions.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => { onSortChange(opt.id); setOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition-colors cursor-pointer ${
                    activeSort === opt.id
                      ? "bg-amber-50 text-amber-700"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <div className={`w-5 h-5 flex items-center justify-center ${activeSort === opt.id ? "text-amber-500" : opt.color}`}>
                    <i className={opt.icon}></i>
                  </div>
                  <span className="flex-1">{opt.label}</span>
                  {opt.badge && (
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                      opt.badge === "Best Seller"
                        ? "bg-amber-100 text-amber-700"
                        : opt.badge === "Sale"
                        ? "bg-red-100 text-red-600"
                        : opt.badge === "Limited"
                        ? "bg-orange-100 text-orange-600"
                        : "bg-emerald-100 text-emerald-700"
                    }`}>
                      {opt.badge}
                    </span>
                  )}
                  {activeSort === opt.id && (
                    <div className="w-4 h-4 flex items-center justify-center text-amber-500">
                      <i className="ri-check-line"></i>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* View toggle */}
        <div className="flex items-center gap-0.5 border border-gray-200 rounded-md overflow-hidden">
          <button
            onClick={() => onViewModeChange("grid")}
            className={`w-8 h-8 flex items-center justify-center transition-colors cursor-pointer ${viewMode === "grid" ? "bg-gray-900 text-white" : "bg-white text-gray-400 hover:text-gray-700"}`}
          >
            <i className="ri-grid-fill text-sm"></i>
          </button>
          <button
            onClick={() => onViewModeChange("list")}
            className={`w-8 h-8 flex items-center justify-center transition-colors cursor-pointer ${viewMode === "list" ? "bg-gray-900 text-white" : "bg-white text-gray-400 hover:text-gray-700"}`}
          >
            <i className="ri-list-check text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
